// uiux-live fetch wrapper.
//
// Hardened per the uiux-runtime/uiuxRuntimeApi.js patterns after the
// sanitizer-drift incident: every failure becomes a typed LiveApiError, and
// requestCollection() NEVER throws — a broken source must degrade only its
// own collection, never blank the app.

const AUTH_TOKEN_STORAGE_KEY = 'authToken';
const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_TIMEOUT_MS = 60_000;
const MAX_TOKEN_LENGTH = 8_192;

export class LiveApiError extends Error {
  constructor(code, message, { status = null, body = null } = {}) {
    super(message);
    this.name = 'LiveApiError';
    this.code = code;
    this.status = status;
    this.body = body;
  }
}

const hasControlCharacters = (value) => Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return code < 32 || code === 127;
});

export const readAuthToken = (storage) => {
  let raw = null;
  try {
    raw = storage ? storage.getItem(AUTH_TOKEN_STORAGE_KEY) : null;
  } catch (_error) {
    return null;
  }
  if (typeof raw !== 'string') return null;
  const token = raw.trim();
  if (!token || token.length > MAX_TOKEN_LENGTH || /\s/.test(token) || hasControlCharacters(token)) {
    return null;
  }
  return token;
};

const boundTimeout = (timeoutMs) => (
  Number.isFinite(Number(timeoutMs)) && Number(timeoutMs) > 0
    ? Math.min(Number(timeoutMs), MAX_TIMEOUT_MS)
    : DEFAULT_TIMEOUT_MS
);

export const createLiveApiClient = ({
  fetchImpl = null,
  storage = null,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) => {
  const resolveFetch = () => {
    if (typeof fetchImpl === 'function') return fetchImpl;
    if (typeof fetch === 'function') return fetch;
    return null;
  };
  const resolveStorage = () => {
    if (storage) return storage;
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    return null;
  };

  // responseType: 'json' (default) parses the body as JSON; 'text' returns
  // the raw body string (CSV attachments like POST /api/list-download/csv —
  // their ERROR responses are still JSON and keep flowing through the
  // LiveApiError body parse below); 'blob' returns the raw Blob for binary
  // attachments (締結済みPDFの GET /api/marketing/contracts/:id/download —
  // 2026-08-19 配線監査W7)。エラー応答は 'text'/'blob' でもJSONのままなので、
  // 下の LiveApiError の body 解析はそのまま効く。
  // body: plain objects/strings are sent as JSON; a FormData body is passed
  // through untouched WITHOUT a Content-Type header so the browser writes
  // the multipart boundary itself (POST /api/client/sales-lists/upload).
  // timeoutMs: 既定 (factory の値、通常15秒) では足りない遅いエンドポイント
  // 用の上書き。例: POST /api/list-download/companies は本番約567万行に対する
  // 5列 LIKE で COUNT(*) だけで実測8.5秒かかるため、呼び出し側が45秒を渡す。
  // 省略時は factory の値のまま。上限は boundTimeout の60秒で、per-request
  // 値でも越えさせない。
  const request = async (path, {
    method = 'GET',
    body,
    idempotencyKey,
    responseType = 'json',
    timeoutMs: requestTimeoutMs,
  } = {}) => {
    const doFetch = resolveFetch();
    if (!doFetch) {
      throw new LiveApiError('live-fetch-unavailable', 'データ取得機能を利用できません。');
    }
    if (typeof path !== 'string' || !path.startsWith('/api')) {
      throw new LiveApiError('live-path-invalid', '同一オリジンの /api 以外へは接続しません。');
    }
    const token = readAuthToken(resolveStorage());
    if (!token) {
      throw new LiveApiError('live-authentication-unavailable', 'ログイン情報を確認できません。');
    }

    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    let serializedBody;
    const isFormDataBody = typeof FormData !== 'undefined' && body instanceof FormData;
    if (isFormDataBody) {
      serializedBody = body;
    } else if (body !== undefined && body !== null) {
      headers['Content-Type'] = 'application/json';
      serializedBody = typeof body === 'string' ? body : JSON.stringify(body);
    }
    if (typeof idempotencyKey === 'string' && idempotencyKey.trim()) {
      headers['Idempotency-Key'] = idempotencyKey.trim();
    }

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    // per-request の指定を優先し、無ければ factory の値。boundTimeout が
    // 数値でない値・0以下を既定へ、60秒超を60秒へ丸める。
    const effectiveTimeoutMs = requestTimeoutMs === undefined ? timeoutMs : requestTimeoutMs;
    const timer = controller ? setTimeout(() => controller.abort(), boundTimeout(effectiveTimeoutMs)) : null;

    try {
      const response = await doFetch(path, {
        method,
        credentials: 'same-origin',
        headers,
        ...(serializedBody !== undefined ? { body: serializedBody } : {}),
        ...(controller ? { signal: controller.signal } : {}),
      });

      if (!response || !response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch (_error) {
          errorBody = null;
        }
        throw new LiveApiError(
          `live-http-${response?.status || 'unknown'}`,
          '実データを取得できません。',
          { status: Number(response?.status) || null, body: errorBody },
        );
      }

      if (response.status === 204) return null;
      if (responseType === 'text') {
        try {
          return await response.text();
        } catch (_error) {
          throw new LiveApiError('live-response-invalid', '応答の形式を確認できません。');
        }
      }
      if (responseType === 'blob') {
        try {
          return await response.blob();
        } catch (_error) {
          throw new LiveApiError('live-response-invalid', '応答の形式を確認できません。');
        }
      }
      try {
        return await response.json();
      } catch (_error) {
        throw new LiveApiError('live-response-invalid', '応答の形式を確認できません。');
      }
    } catch (error) {
      if (error instanceof LiveApiError) throw error;
      if (controller?.signal?.aborted) {
        throw new LiveApiError('live-request-timeout', '実データの取得が時間内に完了しませんでした。');
      }
      throw new LiveApiError('live-request-failed', '実データを取得できません。');
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  // Degradation primitive: never throws. A broken source degrades that one
  // collection ({status:'unavailable', error}) instead of blanking the app.
  const requestCollection = async (name, path, opts = {}, validate = null) => {
    try {
      const data = await request(path, opts);
      let validated = data;
      if (typeof validate === 'function') {
        const result = validate(data);
        if (result === false) {
          throw new LiveApiError(
            'live-collection-invalid',
            `「${name}」のデータ形式を確認できません。`,
          );
        }
        if (result !== undefined && result !== true) validated = result;
      }
      return { status: 'ready', data: validated };
    } catch (error) {
      const typedError = error instanceof LiveApiError
        ? error
        : new LiveApiError('live-collection-invalid', `「${name}」のデータ形式を確認できません。`);
      return { status: 'unavailable', error: typedError };
    }
  };

  return { request, requestCollection };
};

export { AUTH_TOKEN_STORAGE_KEY, DEFAULT_TIMEOUT_MS };
